import React, {Fragment} from "react";
import Page from "../../../../components/BaseComponents/Page/Page";
import {FaSolidIcon} from "../../../../components/FontAwesomeIcons";
import PropTypes from "prop-types";
import {ModuloCassaAPI} from "../../../../api/ModuloCassa/ModuloCassaAPI";
import moment from "moment";
import {Litepicker} from "litepicker";
import "litepicker/dist/plugins/mobilefriendly";
import {EllipsisLoader} from "../../../../components/GenericUI/EllipsisLoader/EllipsisLoader";
import {formatCurrency} from "../../../../helpers/CurrencyHelpers";
import {ALIQUOTE_IVA} from "./ModuloCassaContants";
import {normalizeModuloCassaStatistics} from "../../../../helpers/ModuloCassaStatisticsHelper";
import "./StatisticsPage.css";


export default class StatisticsPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            startDate: moment().format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD"),
            statistics: {},
            fetching: false,
            error: null
        }
        this.startDateInputRef = null;
        this.endDateInputRef = null;
        this.datePickerInstance = null;
    }

    componentDidMount() {
        this.fetchStatistics()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevState.startDate !== this.state.startDate || prevState.endDate !== this.state.endDate) {
            this.fetchStatistics();
        }
    }

    componentWillUnmount() {
        if (this.datePickerInstance) {
            this.datePickerInstance.destroy();
        }
    }

    fetchStatistics = _ => {
        this.setState({fetching: true, error: null})
        ModuloCassaAPI.Dashboard.getStatisticalInformation(this.state.startDate, this.state.endDate)
            .then(data => {
                this.setState({statistics: data || {}, fetching: false})
            })
            .catch(error => {
                this.setState({
                    fetching: false,
                    error: error && error.message ? error.message : (error || "Impossibile recuperare le statistiche")
                })
            })
    }

    handleCheckinRef = node => {
        if (node) {
            this.startDateInputRef = node;
            if (this.startDateInputRef && this.endDateInputRef) {
                this.handleLightpickerInit();
            }
        }
    }

    handleCheckoutRef = node => {
        if (node) {
            this.endDateInputRef = node;
            if (this.startDateInputRef && this.endDateInputRef) {
                this.handleLightpickerInit();
            }
        }
    }

    handleLightpickerInit = _ => {
        if (this.datePickerInstance) {
            this.datePickerInstance.destroy();
        }

        this.datePickerInstance = new Litepicker({
            element: this.startDateInputRef,
            elementEnd: this.endDateInputRef,
            singleMode: false,
            lang: "it-IT",
            format: "DD MMM",
            autoApply: true,
            buttonText: {
                apply: "Conferma",
                cancel: "Annulla"
            },
            tooltipText: {
                one: "giorno",
                other: "giorni"
            },
            lockDays: (this.props.closingDays || []).map(closingDays => {
                return [closingDays.data_inizio, closingDays.data_fine]
            }),
            disallowLockDaysInRange: true,
            plugins: ["mobilefriendly"],
            tooltipNumber: totalDays => {
                return totalDays;
            },
            setup: picker => {
                picker.on("selected", (date1, date2) => {
                    this.setState({
                        startDate: moment(date1.dateInstance).format("YYYY-MM-DD"),
                        endDate: moment((date2 || date1).dateInstance).format("YYYY-MM-DD"),
                    })
                })
            }
        });
    }

    getAliquotaDescription = code => {
        let description = ""
        ALIQUOTE_IVA.forEach(aliquota => {
            if (aliquota.codice === code) {
                description = aliquota.descrizione
            }
        })
        return description || code
    }

    getSelectedPeriodLabel = _ => {
        if (this.state.startDate === this.state.endDate) {
            return moment(this.state.startDate).format("dddd DD MMMM YYYY")
        }

        return moment(this.state.startDate).format("DD MMM") + " - " + moment(this.state.endDate).format("DD MMM YYYY")
    }

    getQueueStatusMessage = statistics => {
        if (statistics.queue.toSend > 0) {
            return "Alcuni scontrini sono ancora in attesa di invio."
        }

        if (statistics.queue.trackedTotal > 0) {
            return "La coda invii risulta allineata."
        }

        return "Nessun invio registrato nella coda per il periodo selezionato."
    }

    getGoalStatusMessage = statistics => {
        if (statistics.goal.target === null && statistics.goal.currentTotal === null) {
            return ""
        }

        if (statistics.goal.difference === null) {
            return "Obiettivo impostato"
        }

        if (statistics.goal.difference <= 0) {
            return "Obiettivo raggiunto"
        }

        return "Mancano " + formatCurrency(statistics.goal.difference, "EUR", "it-IT")
    }

    renderMetricCard = (icon, label, value, detail, modifierClass = "") => {
        return (
            <div className={"col-6 col-md-3 mb-3"}>
                <div className={"card border-0 shadow-sm modulo-cassa-statistics-metric-card " + modifierClass}>
                    <div className={"modulo-cassa-statistics-metric-icon"}>
                        <FaSolidIcon name={icon}/>
                    </div>
                    <div className={"modulo-cassa-statistics-metric-label"}>{label}</div>
                    <div className={"modulo-cassa-statistics-metric-value"}>{value}</div>
                    <div className={"modulo-cassa-statistics-metric-detail"}>{detail}</div>
                </div>
            </div>
        )
    }

    getPaymentMethodsStyle = type => {
        let icon = "fas fa-money-bill"
        let color = "success-soft text-success"
        let progressBarClass = "bg-success"

        switch (parseInt(type)) {
            case 1:
                icon = "fas fa-money-bill"
                color = "success-soft text-success"
                progressBarClass = "bg-success"
                break;
            case 2:
                icon = "fas fa-credit-card"
                color = "info-soft text-info"
                progressBarClass = "bg-info"
                break;
            case 3:
                icon = "fas fa-ban"
                color = "danger-soft text-danger"
                progressBarClass = "bg-danger"
                break;
            case 4:
                icon = "fas fa-concierge-bell"
                color = "warning-soft text-warning"
                progressBarClass = "bg-warning"
                break;
            case 5:
                icon = "fas fa-box"
                color = "warning-soft text-warning"
                progressBarClass = "bg-warning"
                break;
            case 6:
                icon = "fas fa-ticket-alt"
                color = "primary-soft text-primary"
                progressBarClass = "bg-primary"
                break;
            case 7:
                icon = "fas fa-file-invoice"
                color = "secondary-soft text-secondary"
                progressBarClass = "bg-secondary"
                break;
            default:
                break;
        }

        return {color: color, icon: icon, progressBarClass: progressBarClass}
    }

    renderPaymentMethods = statistics => {
        if (!statistics.paymentMethods.length) {
            return (
                <div className={"card card-body shadow-sm border-0 modulo-cassa-statistics-empty-state"}>
                    Nessun metodo di pagamento registrato nel periodo selezionato.
                </div>
            )
        }

        return (
            <div className={"card shadow-sm border-0 overflow-hidden"}>
                <div className={"list-group list-group-flush"}>
                    {statistics.paymentMethods.map((paymentMethod, key) => {
                        const percentage = statistics.total > 0 ? (paymentMethod.importo * 100) / statistics.total : 0
                        const paymentMethodStyle = this.getPaymentMethodsStyle(paymentMethod.tipo)

                        return (
                            <div className={"list-group-item border-0 modulo-cassa-statistics-list-item"} key={key}>
                                <div className={"d-flex justify-content-between align-items-start"}>
                                    <div className={"pr-3"}>
                                        <div className={"modulo-cassa-statistics-list-title"}>
                                            <span className={"modulo-cassa-statistics-payment-icon mr-2 " + paymentMethodStyle.color}>
                                                <i className={paymentMethodStyle.icon}/>
                                            </span>
                                            {paymentMethod.descrizione}
                                        </div>
                                        <small className={"text-muted"}>{percentage.toFixed(1)}% del totale incassato</small>
                                    </div>
                                    <div className={"text-right"}>
                                        <div className={"font-weight-bold"}>{formatCurrency(paymentMethod.importo, "EUR", "it-IT")}</div>
                                        <small className={"text-muted"}>{paymentMethod.tipo ? "Tipo " + paymentMethod.tipo : "Tipo n.d."}</small>
                                    </div>
                                </div>
                                <div className={"progress modulo-cassa-statistics-progress mt-3"}>
                                    <div
                                        className={"progress-bar " + paymentMethodStyle.progressBarClass}
                                        role={"progressbar"}
                                        style={{width: Math.min(percentage, 100) + "%"}}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    renderIvaSummary = statistics => {
        if (!statistics.sent.iva.rows.length) {
            return (
                <div className={"card card-body shadow-sm border-0 modulo-cassa-statistics-empty-state"}>
                    Nessun dettaglio IVA disponibile per il periodo selezionato.
                </div>
            )
        }

        return (
            <div className={"card shadow-sm border-0 overflow-hidden"}>
                <div className={"card-body border-bottom modulo-cassa-statistics-section-summary"}>
                    <div className={"row"}>
                        <div className={"col-6"}>
                            <small className={"text-muted d-block"}>Imponibile totale</small>
                            <div className={"font-weight-bold h5 mb-0"}>
                                {formatCurrency(statistics.sent.iva.imponibile, "EUR", "it-IT")}
                            </div>
                        </div>
                        <div className={"col-6 text-right"}>
                            <small className={"text-muted d-block"}>IVA stimata</small>
                            <div className={"font-weight-bold h5 mb-0 text-danger"}>
                                {formatCurrency(statistics.sent.iva.imposta, "EUR", "it-IT")}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"list-group list-group-flush"}>
                    {statistics.sent.iva.rows.map((iva, key) => {
                        return (
                            <div className={"list-group-item border-0 modulo-cassa-statistics-list-item"} key={key}>
                                <div className={"d-flex justify-content-between align-items-start"}>
                                    <div className={"pr-3"}>
                                        <div className={"modulo-cassa-statistics-list-title"}>
                                            <FaSolidIcon name={"bag-shopping"} className={"text-warning mr-2"}/>
                                            Venduto con IVA {this.getAliquotaDescription(iva.code)}
                                        </div>
                                        <small className={"text-muted"}>Aliquota {iva.code}</small>
                                    </div>
                                    <div className={"text-right"}>
                                        <div className={"font-weight-bold"}>
                                            {formatCurrency(iva.imponibile, "EUR", "it-IT")}
                                        </div>
                                        <small className={"text-muted"}>
                                            {iva.hasImposta ?
                                                "Imposta " + formatCurrency(iva.imposta, "EUR", "it-IT") :
                                                "Imposta non dettagliata"}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    renderGoalCard = statistics => {
        if (statistics.goal.target === null && statistics.goal.currentTotal === null) {
            return null
        }

        const goalCompleted = statistics.goal.difference !== null && statistics.goal.difference <= 0

        return (
            <div className={"col-12 col-lg-6 mb-3"}>
                <div className={"card card-body shadow-sm border-0 modulo-cassa-statistics-goal-card"}>
                    <div className={"d-flex justify-content-between align-items-start"}>
                        <div>
                            <small className={"text-uppercase text-muted d-block"}>Obiettivo periodo</small>
                            <h5 className={"mb-1"}>{this.getGoalStatusMessage(statistics)}</h5>
                            <small className={"text-muted"}>
                                {statistics.goal.period || "Periodo non definito"}
                            </small>
                        </div>
                        <span className={"badge " + (goalCompleted ? "badge-success" : "badge-warning")}>
                            {goalCompleted ? "Raggiunto" : "In corso"}
                        </span>
                    </div>
                    <div className={"row mt-3"}>
                        <div className={"col-6"}>
                            <small className={"text-muted d-block"}>Target</small>
                            <div className={"font-weight-bold"}>
                                {statistics.goal.target !== null ? formatCurrency(statistics.goal.target, "EUR", "it-IT") : "-"}
                            </div>
                        </div>
                        <div className={"col-6 text-right"}>
                            <small className={"text-muted d-block"}>Attuale</small>
                            <div className={"font-weight-bold"}>
                                {statistics.goal.currentTotal !== null ? formatCurrency(statistics.goal.currentTotal, "EUR", "it-IT") : "-"}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    render() {
        const startDateFormatted = this.state.startDate ? moment(this.state.startDate).format("ddd DD MMMM") : ""
        const endDateFormatted = this.state.endDate ? moment(this.state.endDate).format("ddd DD MMMM") : ""
        const statistics = normalizeModuloCassaStatistics(this.state.statistics)
        const queuePercentage = Math.min(statistics.queue.completionPercentage, 100)

        return (
            <Fragment>
                <Page
                    title={"Statistiche"}
                    leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                    onLeftIconClick={this.props.onDismiss}
                    active={true}
                    content={
                        <div className={"modulo-cassa-statistics-page"}>
                            <div className={"modulo-cassa-statistics-hero"}>
                                <div className={"container py-4"}>
                                    <small className={"text-uppercase modulo-cassa-statistics-eyebrow"}>Statistiche scontrini</small>
                                    <h4 className={"text-white mb-1"}>{this.getSelectedPeriodLabel()}</h4>
                                    <p className={"modulo-cassa-statistics-hero-description mb-3"}>
                                        Panoramica di invii, importi, metodi di pagamento e riepilogo IVA.
                                    </p>

                                    <div className={"card card-body shadow-sm border-0 modulo-cassa-statistics-date-card"}>
                                        <small className={"text-muted text-uppercase mb-2"}>Scegli periodo</small>
                                        <div className={"row align-items-center"}>
                                            <div className={"col-6 text-dark"}>
                                                <b>A partire dal</b>
                                                <div
                                                    ref={this.handleCheckinRef}
                                                    className={(!startDateFormatted ? "text-muted " : "") + "text-capitalize cursor-pointer modulo-cassa-statistics-date-value"}>
                                                    {startDateFormatted || "Clicca qui"}
                                                </div>
                                            </div>
                                            <div className={"col-6 text-dark"}>
                                                <b>Fino al</b>
                                                <div
                                                    ref={this.handleCheckoutRef}
                                                    className={(!endDateFormatted ? "text-muted " : "") + "text-capitalize cursor-pointer modulo-cassa-statistics-date-value"}>
                                                    {endDateFormatted || "Clicca qui"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {this.state.fetching &&
                                <div className={"mt-5 w-100 text-center"}>
                                    <EllipsisLoader/>
                                </div>
                            }

                            {!this.state.fetching &&
                                <div className={"container pb-4"}>
                                    {this.state.error &&
                                        <div className={"alert alert-danger mt-3 mb-0"}>
                                            {this.state.error}
                                        </div>
                                    }

                                    {!this.state.error &&
                                        <div className={"row modulo-cassa-statistics-content"}>
                                            {this.renderMetricCard(
                                                "receipt",
                                                "Scontrini nel periodo",
                                                statistics.number,
                                                statistics.number === 1 ? "Movimento registrato" : "Movimenti registrati",
                                                "modulo-cassa-statistics-metric-card-info"
                                            )}
                                            {this.renderMetricCard(
                                                "euro-sign",
                                                "Incasso totale",
                                                formatCurrency(statistics.total, "EUR", "it-IT"),
                                                "Totale movimentato nel periodo",
                                                "modulo-cassa-statistics-metric-card-success"
                                            )}
                                            {this.renderMetricCard(
                                                "receipt",
                                                "Inviati all'AE",
                                                statistics.sent.number,
                                                formatCurrency(statistics.sent.total, "EUR", "it-IT"),
                                                "modulo-cassa-statistics-metric-card-success"
                                            )}
                                            {this.renderMetricCard(
                                                "clock",
                                                "In coda invio",
                                                statistics.queue.toSend,
                                                statistics.queue.toSend > 0 ? "Da sincronizzare" : "Nessuna attesa",
                                                "modulo-cassa-statistics-metric-card-warning"
                                            )}
                                            {this.renderMetricCard(
                                                "receipt",
                                                "Creati",
                                                statistics.created.number,
                                                statistics.created.number > 0 ? formatCurrency(statistics.created.total, "EUR", "it-IT") : "Nessun nuovo scontrino",
                                                "modulo-cassa-statistics-metric-card-warning"
                                            )}
                                            {this.renderMetricCard(
                                                "receipt",
                                                "Annullati",
                                                statistics.cancelled.number,
                                                statistics.cancelled.number > 0 ? formatCurrency(statistics.cancelled.total, "EUR", "it-IT") : "Nessun annullamento",
                                                "modulo-cassa-statistics-metric-card-danger"
                                            )}
                                            {this.renderMetricCard(
                                                "delete-left",
                                                "Resi",
                                                parseFloat(statistics.returns).toFixed(2),
                                                statistics.returns > 0 ? "Presenti nel periodo" : "Nessun reso",
                                                "modulo-cassa-statistics-metric-card-dark"
                                            )}
                                            {this.renderMetricCard(
                                                "euro-sign",
                                                "IVA stimata",
                                                formatCurrency(statistics.sent.iva.imposta, "EUR", "it-IT"),
                                                "Calcolata sugli invii all'AE",
                                                "modulo-cassa-statistics-metric-card-danger"
                                            )}

                                            <div className={"col-12 col-lg-6 mb-3"}>
                                                <div className={"card card-body shadow-sm border-0 modulo-cassa-statistics-queue-card"}>
                                                    <div className={"d-flex justify-content-between align-items-start"}>
                                                        <div>
                                                            <small className={"text-uppercase text-muted d-block"}>Coda invii</small>
                                                            <h5 className={"mb-1"}>{this.getQueueStatusMessage(statistics)}</h5>
                                                            <small className={"text-muted"}>
                                                                {statistics.queue.startDate ?
                                                                    "Ultimo avvio: " + moment(statistics.queue.startDate).format("DD MMM YYYY HH:mm") :
                                                                    "Nessuna data di sincronizzazione disponibile"}
                                                            </small>
                                                        </div>
                                                        <span className={"badge " + (statistics.queue.toSend > 0 ? "badge-warning" : "badge-success")}>
                                                            {statistics.queue.toSend > 0 ? "Da inviare" : "Allineata"}
                                                        </span>
                                                    </div>
                                                    <div className={"progress modulo-cassa-statistics-progress mt-3"}>
                                                        <div
                                                            className={"progress-bar " + (statistics.queue.toSend > 0 ? "bg-warning" : "bg-success")}
                                                            role={"progressbar"}
                                                            style={{width: queuePercentage + "%"}}
                                                        />
                                                    </div>
                                                    <div className={"row mt-3"}>
                                                        <div className={"col-6"}>
                                                            <small className={"text-muted d-block"}>Inviati dalla coda</small>
                                                            <div className={"font-weight-bold"}>{statistics.queue.sent}</div>
                                                        </div>
                                                        <div className={"col-6 text-right"}>
                                                            <small className={"text-muted d-block"}>Da inviare</small>
                                                            <div className={"font-weight-bold text-warning"}>{statistics.queue.toSend}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {this.renderGoalCard(statistics)}

                                            <div className={"col-12 col-lg-6 mb-3"}>
                                                <div className={"modulo-cassa-statistics-section-header"}>
                                                    <small className={"text-uppercase text-muted"}>Metodi di pagamento</small>
                                                    <h5 className={"mb-0"}>Distribuzione degli incassi</h5>
                                                </div>
                                                {this.renderPaymentMethods(statistics)}
                                            </div>

                                            <div className={"col-12 col-lg-6 mb-3"}>
                                                <div className={"modulo-cassa-statistics-section-header"}>
                                                    <small className={"text-uppercase text-muted"}>Riepilogo IVA</small>
                                                    <h5 className={"mb-0"}>Imponibile e imposta</h5>
                                                </div>
                                                {this.renderIvaSummary(statistics)}
                                            </div>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }
                />
            </Fragment>
        );
    }
}


StatisticsPage.propTypes = {
    userNegozio: PropTypes.object,
    onDismiss: PropTypes.func,
    closingDays: PropTypes.array
}
